import type { TextBlockModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";

import FontSizeModifierComponent from "./FontSizeModifier";
import FontStyleControls from "./FontStyleControls";
import FontWeightModifierComponent from "./FontWeightModifier";
import LineHeightModifierComponent from "./LineHeightModifier";
import TextSpacingModifierComponent from "./TextSpacingModifier";

const STYLE_LIBRARY_TAGS = new Set(["h1", "h2", "h3", "h4", "p"]);

interface StyleModifierProps {
  onChange: (modifiers: TextBlockModifierValue[]) => void;
}

const StyleModifier: React.FC<StyleModifierProps> = ({ onChange }) => {
  const defaultDesignation = useDefaultSelectedElementDesignation();
  const tagName = defaultDesignation?.element.tagName;
  const stableElementId = defaultDesignation?.element.stableElementId;
  const hasStyleLibrary = STYLE_LIBRARY_TAGS.has(tagName ?? "");

  const [savedStyleMode, setSavedStyleMode] = React.useState(hasStyleLibrary);

  // NOTE (Nick, 2025-12-18): Sync savedStyleMode to show if has a style library tag (h1/h2/h3/h4/p)
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable element id needed to switch state when element changes
  React.useEffect(() => {
    setSavedStyleMode(hasStyleLibrary);
  }, [hasStyleLibrary, stableElementId]);

  return (
    <div className="space-y-4">
      <div className="flex w-full flex-col gap-2">
        <FontStyleControls
          onChange={onChange}
          savedStyleMode={savedStyleMode}
          setSavedStyleMode={setSavedStyleMode}
        />
        {!savedStyleMode && (
          <div className="grid grid-cols-2 gap-2">
            <FontWeightModifierComponent onChange={onChange} />
            <FontSizeModifierComponent onChange={onChange} />
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-medium text-muted">Line</span>
              <LineHeightModifierComponent onChange={onChange} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-medium text-muted">Spacing</span>
              <TextSpacingModifierComponent onChange={onChange} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleModifier;
